import { useEffect, useState } from "react";
import { SelectInput, TextInput, useNotify } from "react-admin";
import {
  Grid,
  Button,
  Tabs,
  Tab,
  Box,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardHeader,
  Tooltip,
  CardContent,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { apiSettingsApi, configSettingsApi, environVariablesApi } from "../../providers/env";
import { useParams } from "react-router";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CustomSelectInput from "./components/CustomSelectInput";
import { useCustomPermissions } from "../../hooks/usePermissions";

export const TestApiForm = (props: any) => {
  const { duplicate } = props;
  const { id, agentId } = useParams();
  const [tabIndex, setTabIndex] = useState(0);
  const [params, setParams] = useState([{ key: "", value: "" }]);
  const [header, setHeaders] = useState([{ key: "", value: "" }]);
  const [verificationParams, setVerificationParams] = useState([{ key: "{username}", value: "" }]);
  const [openModal, setOpenModal] = useState(false);
  const [description, setDescription] = useState("");
  const [keyword, setKeyword] = useState("");
  const notify = useNotify();
  const [data, setData] = useState<any>(null);
  const form = useForm({ defaultValues: data || {} });
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState<any>();
  const [paramDatas, setParamDatas] = useState<any>();
  const { hasPermission } = useCustomPermissions();
  const [prechatLabel, setPrechatLabel] = useState("");

  const canCreate = hasPermission('api_management', 'create');

  const agentIdLocal =
    agentId ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();
  const fetchRatingsReport = async () => {
    try {
      const res = await environVariablesApi.getVariables({
        agentId: agentIdLocal,
      });
      setParamDatas(res.data?.data);
    } catch (err) {
      console.error("Error fetching config settings", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchPrechatLabel = async () => {
      if (!agentIdLocal) return;
      try {
        const res = await configSettingsApi.getLivechatConfig({ agentId: agentIdLocal });
        const label = (res.data?.PreFormField || "").trim();
        setPrechatLabel(label);
        if (!id && label) {
          setVerificationParams((prev) => {
            const updated = [...prev];
            const idx = updated.findIndex((p) =>
              p.key && (p.key.toLowerCase() === "{username}" || p.key.toLowerCase() === "username")
            );
            if (idx >= 0) {
              const currentValue = (updated[idx].value || "").trim();
              if (!currentValue || currentValue.toLowerCase() === "nama") {
                updated[idx] = { ...updated[idx], value: label };
              }
            } else {
              updated.unshift({ key: "{username}", value: label });
            }
            return updated;
          });
        }
      } catch (err) {
        console.error("Error fetching livechat config", err);
      }
    };

    fetchPrechatLabel();
  }, [agentIdLocal, id]);

  useEffect(() => {
    if (id) {
      const fetchConfig = async () => {
        try {
          const res = await apiSettingsApi.getVendorApiDetail({
            vendorApiId: parseInt(id) || 0,
          });
          setParams(res.data.vendor_api_params && res.data.vendor_api_params.length > 0 ? res.data.vendor_api_params : [{ key: "", value: "" }]);
          setHeaders(res.data.vendor_api_headers && res.data.vendor_api_headers.length > 0 ? res.data.vendor_api_headers : [{ key: "", value: "" }]);
          const loadedParams = res.data.vendor_api_verification_params && res.data.vendor_api_verification_params.length > 0 
            ? res.data.vendor_api_verification_params
            : [{ key: "{username}", value: prechatLabel || "" }];
          // Ensure {username} is at the top
          const usernameParam = loadedParams.find((p: any) => 
            p.key && (p.key.toLowerCase() === "{username}" || p.key.toLowerCase() === "username")
          );
          const otherParams = loadedParams.filter((p: any) => 
            !p.key || (p.key.toLowerCase() !== "{username}" && p.key.toLowerCase() !== "username")
          );
          
          if (usernameParam) {
            setVerificationParams([usernameParam, ...otherParams]);
          } else {
            setVerificationParams([{ key: "{username}", value: prechatLabel || "" }, ...otherParams]);
          }
          setData(res.data);
        } catch (err) {
          console.error("Error fetching config settings", err);
        } finally {
          setLoading(false);
        }
      };

      fetchConfig();
    }
  }, [id]);

  const isOpenModal = (values: any) => {
    setOpenModal(!openModal);
  };

  const handleSubmit = async () => {
    const values = form.getValues();

    const trimmedParams = params.map((p) => ({
      key: p.key?.trim() || "",
      value: p.value?.trim() || "",
    }));

    const trimmedHeaders = header.map((h) => ({
      key: h.key?.trim() || "",
      value: h.value?.trim() || "",
    }));

    const trimmedVerificationParams = verificationParams.map((vp) => ({
      key: vp.key?.trim() || "",
      value: vp.value?.trim() || "",
    }));

    let apiWithBraces = values.api;

    const fullData = {
      ...values,
      api: apiWithBraces,
      description: `Use this api for ${description.trim()}`,
      keyword: keyword.trim(),
      vendor_api_params: trimmedParams,
      vendor_api_headers: trimmedHeaders,
      vendor_api_verification_params: trimmedVerificationParams,
      agent_id: agentIdLocal,
    };

    try {
      if (id && duplicate !== true) {
        await apiSettingsApi.updateVendorApi({
          vendorApiId: parseInt(id),
          vendorApiMasterUpdate: fullData,
        });
        notify("Updated successfully", { type: "success" });
      } else {
        const { id, ...rest } = fullData;
        await apiSettingsApi.createVendorApi({
          vendorApiMasterCreate: rest,
        });
        notify("Created successfully", { type: "success" });
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || error?.message || "An error occurred";
      notify(errorMessage, { type: "error" });
    }
  };

  const handleParamChange = (
    index: number,
    field: "key" | "value",
    newValue: string
  ) => {
    setParams((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: newValue };
      return updated;
    });
  };

  const handleAddParam = () => setParams([...params, { key: "", value: "" }]);
  const handleRemoveParam = (index: number) =>
    setParams(params.filter((_, i) => i !== index));

  const handleAddHeader = () => setHeaders([...header, { key: "", value: "" }]);
  const handleRemoveHeader = (index: number) =>
    setHeaders(header.filter((_, i) => i !== index));

  const handleHeaderChange = (
    index: number,
    field: "key" | "value",
    value: string
  ) => {
    setHeaders((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Helper function to ensure {username} is always at the top
  const ensureUsernameAtTop = (params: any[]) => {
    const usernameParam = params.find((p: any) => 
      p.key && (p.key.toLowerCase() === "{username}" || p.key.toLowerCase() === "username")
    );
    const otherParams = params.filter((p: any) => 
      !p.key || (p.key.toLowerCase() !== "{username}" && p.key.toLowerCase() !== "username")
    );
    
    if (usernameParam) {
      return [usernameParam, ...otherParams];
    } else {
      // If no username param exists, add default one at the top
      return [{ key: "{username}", value: prechatLabel || "" }, ...otherParams];
    }
  };

  const handleVerificationParamChange = (
    index: number,
    field: "key" | "value",
    newValue: string
  ) => {
    const updated = [...verificationParams];
    updated[index][field] = newValue;
    // Ensure username stays at top after update
    const reordered = ensureUsernameAtTop(updated);
    setVerificationParams(reordered);
  };

  const handleAddVerificationParam = () => {
    const newParams = [...verificationParams, { key: "", value: "" }];
    setVerificationParams(ensureUsernameAtTop(newParams));
  };
  
  const handleRemoveVerificationParam = (index: number) => {
    const param = verificationParams[index];
    // Prevent deleting the default {username} entry
    if (param.key && (param.key.toLowerCase() === "{username}" || param.key.toLowerCase() === "username")) {
      return; // Don't allow deletion
    }
    const updated = verificationParams.filter((_, i) => i !== index);
    setVerificationParams(ensureUsernameAtTop(updated));
  };

  const handleTabChange = (_: any, newValue: number) => setTabIndex(newValue);

  const AuthorizationFields = () => {
    const authType = useWatch({ name: "auth_type", control: form.control });
    // watch username từ react-hook-form
    const usernameValue =
      useWatch({ name: "username", control: form.control }) ?? "";

    if (authType === "Basic Auth") {
      return (
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              freeSolo
              options={paramDatas?.map((d: any) => d.key) ?? []}
              // value bind với form
              value={usernameValue}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === "input") {
                  // khi nhập, cập nhật username trong form (không phải secret)
                  form.setValue("username", newInputValue, {
                    shouldDirty: true,
                  });
                }
                if (reason === "clear") {
                  form.setValue("username", "", { shouldDirty: true });
                  form.setValue("secret", "", { shouldDirty: true });
                }
              }}
              onChange={(event, newValue) => {
                const rawKey = (newValue ?? "").toString().trim();
                if (!rawKey) return;

                // set username (lưu raw key)
                form.setValue("username", rawKey, { shouldDirty: true });

                // nếu có map từ paramDatas -> autofill secret
                const found = paramDatas?.find(
                  (d: any) =>
                    (d.key ?? "").toString().replace(/[{}]/g, "").toLowerCase() ===
                    rawKey.replace(/[{}]/g, "").toLowerCase()
                );
                if (found) {
                  form.setValue("secret", found.value ?? "", {
                    shouldDirty: true,
                  });
                }
              }}
              renderInput={(params) => (
                // Dùng MUI TextField ở đây để tránh xung đột context
                <TextField
                  {...params}
                  label="Username"
                  fullWidth
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "off", // Chặn trình duyệt gợi ý
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Password"
              fullWidth
              type="password"
              value={form.watch("secret") ?? ""}
              onChange={(e) =>
                form.setValue("secret", e.target.value, { shouldDirty: true })
              }
              inputProps={{
                autoComplete: "new-password", // Chặn trình duyệt gợi ý password
              }}
            />
          </Grid>
        </Grid>
      );
    }

    return (
      <Box mt={2}>
        <TextInput
          source="token"
          label="Enter Bearer Token"
          fullWidth
          multiline
          minRows={4}
        />
      </Box>
    );
  };

  const handleSendApi = async () => {
    try {
      const values = form.getValues();

      const trimmedParams = params.map((p) => ({
        key: p.key?.trim() || "",
        value: p.value?.trim() || "",
      }));

      const trimmedHeaders = header.map((h) => ({
        key: h.key?.trim() || "",
        value: h.value?.trim() || "",
      }));

      const trimmedVerificationParams = verificationParams.map((vp) => ({
        key: vp.key?.trim() || "",
        value: vp.value?.trim() || "",
      }));

      const fullData = {
        ...values,
        description: `Use this api for ${description.trim()}`,
        keyword: keyword.trim(),

        vendor_api_params: trimmedParams,
        vendor_api_headers: trimmedHeaders,
        vendor_api_verification_params: trimmedVerificationParams,
      };

      const res = await apiSettingsApi.proxyRequest({
        vendorApiMasterRequest: fullData,
      });
      setResponse(
        res.data?.body
          ? JSON.stringify(JSON.parse(res.data?.body), null, 2)
          : ""
      );
    } catch (err) {
      console.error("Error fetching config settings", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data) {
      form.reset({
        ...data,
        api: data.api || "",
      });
      if (duplicate !== true) {
        setKeyword(data?.keyword);
        setDescription(
          data?.description?.replace(/Use this api for/gi, "").trim() || ""
        );
      }
    }
  }, [data]);

  useEffect(() => {
    fetchRatingsReport();
  }, []);


  const KeywordSelector = ({
    value,
    onChange,
  }: {
    value: string;
    onChange: (val: string) => void;
  }) => {
    const initOptions = () => {
      if (typeof value === "string") {
        return value.split(",").filter((v) => v.trim() !== "");
      }
      return [];
    };

    const [options, setOptions] = useState<string[]>(initOptions);

    const parsedValue = value
      ? value.split(",").filter((v) => v.trim() !== "")
      : [];

    useEffect(() => {
      // Ensure that options include parsedValue
      setOptions((prev) => Array.from(new Set([...prev, ...parsedValue])));
    }, [value]);

    const handleChange = (_: any, newValue: string[]) => {
      const cleaned = newValue.map((v) => v.trim()).filter((v) => v !== "");

      const merged = Array.from(new Set([...options, ...cleaned]));
      setOptions(merged);
      onChange(cleaned.join(","));
    };

    return (
      <Autocomplete
        multiple
        freeSolo
        filterSelectedOptions
        options={options}
        value={parsedValue}
        onChange={handleChange}
        renderInput={(params) => <TextField {...params} label="" fullWidth />}
        style={{ marginBottom: 8, width: "100%" }}
      />
    );
  };

  return (
    <Card sx={{ m: 2 }}>
      <CardHeader title="Manage Apis" />
      <CardContent>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(isOpenModal)}>
            <>
              <Grid container rowSpacing={4} columnSpacing={2}>
                <Grid item xs={12} md={2}>
                  <SelectInput
                    source="verb"
                    label="Method"
                    fullWidth
                    choices={[
                      { id: "GET", name: "GET" },
                      { id: "POST", name: "POST" },
                    ]}
                    defaultValue="GET"
                  />
                </Grid>
                <Grid item xs={12} md={9}>
                  <TextInput source="api" label="Enter URL" fullWidth />
                </Grid>
                <Grid item xs={12} md={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    style={{ height: 48, marginBottom: 4, marginTop: 8 }}
                    fullWidth
                    onClick={handleSendApi}
                  >
                    Send
                  </Button>
                </Grid>
              </Grid>

              <Box
                sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}
              >
                <Tabs value={tabIndex} onChange={handleTabChange}>
                  <Tab label="Params" />
                  <Tab label="Authorization" />
                  <Tab label="Headers" />
                  <Tab label="Verification Params" />
                  <Tab label="Body" />
                </Tabs>
              </Box>

              <Box
                mt={2}
                sx={{
                  width: "100%",
                  height: 300,
                  overflowY: "auto",
                  overflowX: "hidden",
                  pr: 1,
                }}
              >
                {tabIndex === 0 && (
                  <Box>
                    {params.map((param, index) => (
                      <Grid
                        container
                        spacing={2}
                        key={index}
                        alignItems="center"
                        mb={1}
                      >
                        <Grid item xs={5}>
                          <Autocomplete
                            freeSolo
                            options={paramDatas?.map((d: any) => d.key) ?? []}
                            value={param.key ?? ""}
                            onInputChange={(event, newInputValue, reason) => {
                              if (reason === "input") {
                                handleParamChange(index, "key", newInputValue);
                              }
                              if (reason === "clear") {
                                handleParamChange(index, "key", "");
                                handleParamChange(index, "value", "");
                              }
                            }}
                            onChange={(event, newValue) => {
                              const rawKey = (newValue ?? "").toString().trim();
                              if (!rawKey) return;

                              const braceKey = `{${rawKey.replace(/[{}]/g, "")}}`;
                              const found = paramDatas?.find(
                                (d: any) =>
                                  (d.key ?? "").toString().replace(/[{}]/g, "").toLowerCase() ===
                                  rawKey.replace(/[{}]/g, "").toLowerCase()
                              );

                              setParams((prev) => {
                                const updated = [...prev];
                                updated[index] = {
                                  ...updated[index],
                                  key: braceKey,
                                  value: found ? found.value : updated[index].value,
                                };
                                return updated;
                              });
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Param Key"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={5.5}>
                          <TextField
                            label="Param Value"
                            value={param.value}
                            onChange={(e) =>
                              handleParamChange(index, "value", e.target.value)
                            }
                            fullWidth
                          />
                        </Grid>
                        {(param.key || index !== 0) && (
                          <Grid
                            item
                            xs={2}
                            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                          >
                            {param.key && (
                              <Tooltip title="Copy key">
                                <IconButton
                                  onClick={() => {
                                    if (param.key) {
                                      navigator.clipboard.writeText(param.key);
                                    }
                                  }}
                                  size="small"
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveParam(index)}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        )}
                      </Grid>
                    ))}
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddParam}
                    >
                      Add Param
                    </Button>
                  </Box>
                )}

                {tabIndex === 1 && (
                  <Box>
                    <CustomSelectInput
                      source="auth_type"
                      label="Authorization Type"
                      choices={[
                        { id: "Bearer Token", name: "Bearer Token" },
                        { id: "Basic Auth", name: "Basic Auth" },
                      ]}
                      defaultValue="Bearer Token"
                    />
                    <AuthorizationFields />
                  </Box>
                )}

                {tabIndex === 2 && (
                  <Box>
                    {header.map((item, index) => (
                      <Grid
                        container
                        spacing={2}
                        key={index}
                        alignItems="center"
                        mb={1}
                      >
                        <Grid item xs={5}>
                          <Autocomplete
                            freeSolo
                            options={paramDatas?.map((d: any) => d.key) ?? []}
                            value={item.key ?? ""}
                            onInputChange={(event, newInputValue, reason) => {
                              if (reason === "input") {
                                handleHeaderChange(index, "key", newInputValue);
                              }
                              if (reason === "clear") {
                                handleHeaderChange(index, "key", "");
                                handleHeaderChange(index, "value", "");
                              }
                            }}
                            onChange={(event, newValue) => {
                              const rawKey = (newValue ?? "").toString().trim();
                              if (!rawKey) return;

                              const braceKey = `${rawKey.replace(/[{}]/g, "")}`;
                              const found = paramDatas?.find(
                                (d: any) =>
                                  (d.key ?? "").toString().replace(/[{}]/g, "").toLowerCase() ===
                                  rawKey.replace(/[{}]/g, "").toLowerCase()
                              );

                              setHeaders((prev) => {
                                const updated = [...prev];
                                updated[index] = {
                                  ...updated[index],
                                  key: braceKey,
                                  value: found ? found.value : updated[index].value,
                                };
                                return updated;
                              });
                            }}
                            renderInput={(headers) => (
                              <TextField
                                {...headers}
                                label="Header Key"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={5.5}>
                          <TextField
                            label="Header Value"
                            value={item.value}
                            onChange={(e) =>
                              handleHeaderChange(index, "value", e.target.value)
                            }
                            fullWidth
                          />
                        </Grid>
                        {(item.key || index !== 0) && (
                          <Grid
                            item
                            xs={2}
                            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                          >
                            {item.key && (
                              <Tooltip title="Copy key">
                                <IconButton
                                  onClick={() => {
                                    if (item.key) {
                                      navigator.clipboard.writeText(item.key);
                                    }
                                  }}
                                  size="small"
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveHeader(index)}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        )}
                      </Grid>
                    ))}
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddHeader}
                    >
                      Add Header
                    </Button>
                  </Box>
                )}

                {tabIndex === 3 && (
                  <Box>
                    {verificationParams.map((param, index) => {
                      const isUsernameParam = param.key && (
                        param.key.toLowerCase() === "{username}" || 
                        param.key.toLowerCase() === "username"
                      );
                      const isDefaultUsername = index === 0 && isUsernameParam;
                      
                      return (
                      <Grid
                        container
                        spacing={2}
                        key={index}
                        alignItems="center"
                        mb={1}
                      >
                        <Grid item xs={5}>
                          {isDefaultUsername ? (
                            <TextField
                              label="Verification Param Key"
                              value={param.key ?? ""}
                              disabled
                              fullWidth
                            />
                          ) : (
                            <Autocomplete
                              freeSolo
                              options={paramDatas?.map((d: any) => d.key) ?? []}
                              value={param.key ?? ""}
                              onInputChange={(event, newInputValue, reason) => {
                                if (reason === "input") {
                                  handleVerificationParamChange(index, "key", newInputValue);
                                  // If key is domain, sync value to match
                                  const rawKey = newInputValue.replace(/[{}]/g, "");
                                  if (rawKey === "domain") {
                                    handleVerificationParamChange(index, "value", "domain");
                                  }
                                }
                                if (reason === "clear") {
                                  handleVerificationParamChange(index, "key", "");
                                  handleVerificationParamChange(index, "value", "");
                                }
                              }}
                              onChange={(event, newValue) => {
                                const rawKey = (newValue ?? "").toString().trim();
                                if (!rawKey) return;

                                // For domain, don't add braces
                                const finalKey = rawKey === "domain" ? "domain" : `{${rawKey.replace(/[{}]/g, "")}}`;
                                const oldKey = param.key;
                                handleVerificationParamChange(index, "key", finalKey);

                                // If key is domain, set value to match key (without braces)
                                if (rawKey === "domain" || rawKey.replace(/[{}]/g, "") === "domain") {
                                  handleVerificationParamChange(index, "value", "domain");
                                } else {
                                  const found = paramDatas?.find(
                                    (d: any) =>
                                      d.key === rawKey.replace(/[{}]/g, "")
                                  );
                                  if (found) {
                                    handleVerificationParamChange(index, "value", found.value);
                                  }
                                }

                                const currentApi = form.getValues("api") ?? "";
                                if (oldKey) {
                                  const noBraceOld = oldKey.replace(/[{}]/g, "");
                                  let updatedApi = currentApi
                                    .replaceAll(oldKey, finalKey)
                                    .replaceAll(`{${noBraceOld}}`, finalKey)
                                    .replaceAll(noBraceOld, finalKey);
                                  if (updatedApi !== currentApi) {
                                    form.setValue("api", updatedApi);
                                  }
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Verification Param Key"
                                  fullWidth
                                />
                              )}
                            />
                          )}
                        </Grid>
                        <Grid item xs={5}>
                          {(() => {
                            if (typeof param.value === "string") {
                              const values = param.value.split(",").map(v => v.trim());
                              return values.includes("domain");
                            }
                            return param.value === "domain";
                          })() ? (
                            <TextField
                              label="Chat Value"
                              value={param.value || "domain"}
                              disabled={true}
                              fullWidth
                            />
                          ) : (
                            <Autocomplete
                              multiple
                              freeSolo
                              disableClearable={false}
                              options={["domain"]}
                              value={
                                typeof param.value === "string" && param.value
                                  ? param.value.split(",").map((v) => v.trim()).filter((v) => v)
                                  : Array.isArray(param.value)
                                    ? param.value.filter((v: string) => v.trim() !== "")
                                    : []
                              }
                              onChange={(event, newValue) => {
                                const cleaned = newValue.map((v) => v.toString().trim()).filter((v) => v);
                                // If "domain" is selected, remove all other values and keep only "domain"
                                if (cleaned.includes("domain")) {
                                  handleVerificationParamChange(index, "value", "domain");
                                } else {
                                  const joined = cleaned.join(",");
                                  handleVerificationParamChange(index, "value", joined);
                                }
                              }}
                              disabled={(() => {
                                const vals =
                                  typeof param.value === "string" && param.value
                                    ? param.value.split(",").map((v) => v.trim())
                                    : Array.isArray(param.value)
                                      ? param.value
                                      : [];
                                return vals.includes("domain");
                              })()}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Chat Value"
                                  fullWidth
                                />
                              )}
                            />
                          )}
                        </Grid>
                        {(param.key || index !== 0) && !isDefaultUsername && (
                          <Grid
                            item
                            xs={2}
                            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                          >
                            {param.key && (
                              <Tooltip title="Copy key">
                                <IconButton
                                  onClick={() => {
                                    if (param.key) {
                                      navigator.clipboard.writeText(param.key);
                                    }
                                  }}
                                  size="small"
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveVerificationParam(index)}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        )}
                        {isDefaultUsername && (
                          <Grid
                            item
                            xs={2}
                            sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}
                          >
                            {param.key && (
                              <Tooltip title="Copy key">
                                <IconButton
                                  onClick={() => {
                                    if (param.key) {
                                      navigator.clipboard.writeText(param.key);
                                    }
                                  }}
                                  size="small"
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* Delete button is hidden for default username entry */}
                          </Grid>
                        )}
                      </Grid>
                    );
                    })}
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddVerificationParam}
                    >
                      Add Key
                    </Button>
                  </Box>
                )}

                {tabIndex === 4 && (
                  <TextInput
                    source="body"
                    label="Enter request body"
                    fullWidth
                    multiline
                    minRows={4}
                  />
                )}
              </Box>

              <Box sx={{ width: "100%" }}>
                <Typography variant="h6" gutterBottom>
                  Response
                </Typography>
                <TextField
                  value={response || " "}
                  InputProps={{
                    readOnly: true,
                  }}
                  label="Response body"
                  fullWidth
                  multiline
                  minRows={4}
                />
              </Box>

              <Dialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle sx={{ fontSize: 20 }}>Description</DialogTitle>
                <DialogContent style={{ paddingBottom: 0 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{
                        whiteSpace: "nowrap",
                        marginBottom: 0,
                        minWidth: 120,
                      }}
                    >
                      Use this api for
                    </Typography>
                    <TextField
                      label="Description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      fullWidth
                      multiline
                    />
                  </Box>
                </DialogContent>
                <DialogContent>
                  <Box
                    sx={{
                      padding: "8px",
                      backgroundColor: "#f8d7da",
                      border: "1px solid #f5c6cb",
                      borderRadius: "4px",
                      marginBottom: "14px",
                    }}
                  >
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{
                        whiteSpace: "nowrap",
                        marginBottom: 0,
                      }}
                    >
                      Tags should be comma-separated. Example:{" "}
                      <strong>tag1, tag2, tag3</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{
                        whiteSpace: "nowrap",
                        marginBottom: 0,
                        minWidth: 120,
                      }}
                    >
                      Keyword
                    </Typography>
                    <KeywordSelector value={keyword} onChange={setKeyword} />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setOpenModal(false)}>Cancel</Button>
                  <Button
                    onClick={() => {
                      setOpenModal(false);
                      handleSubmit();
                    }}
                    variant="contained"
                  >
                    Submit
                  </Button>
                </DialogActions>
              </Dialog>
              {canCreate && (
                <Box mt={4} display="flex" justifyContent="flex-start">
                  <Button type="submit" variant="contained" color="primary">
                    Save
                  </Button>
                </Box>
              )}
            </>
          </form>
        </FormProvider>
      </CardContent>
    </Card>
  );
};
